import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, formatApiError } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Send, Music, X, Loader2, Check, AlertTriangle } from "lucide-react";

/** Post-to-TikTok modal (Phase 5.2b MVP).
 * Lists the user's connected accounts as multi-select checkboxes, lets them
 * edit the caption, submits to POST /api/tiktok/post, then polls
 * /api/tiktok/posts every 3s to surface live status until each post lands. */
export default function PostToTikTokModal({ video, onClose }) {
  const [accounts, setAccounts] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [caption, setCaption] = useState("");
  const [posting, setPosting] = useState(false);
  const [results, setResults] = useState(null); // [{tiktok_account_id, status, error, ...}]
  const [environment, setEnvironment] = useState("sandbox");
  // Phase 5.2c-pre — surfaced when backend returns {error:"missing_storage"}.
  const [missingStorage, setMissingStorage] = useState(null);
  const [rerendering, setRerendering] = useState(false);

  const rerenderAndRetry = async () => {
    setRerendering(true);
    try {
      const body = {
        product_name: video?.product?.title || video?.script?.hook?.slice(0, 60) || "video",
        product_description: video?.script?.cta || video?.script?.beat1 || "",
        niche: video?.niche || "general",
      };
      const r = await axios.post(`${API}/video/faceless`, body);
      toast.success(`Re-render queued (job ${r.data?.job_id?.slice(0,8) || "?"}). Wait ~1 min, then post.`);
      setMissingStorage(null);
      onClose?.();
    } catch (e) {
      toast.error(`Re-render failed: ${e?.response?.data?.error || e.message}`);
    } finally { setRerendering(false); }
  };

  // Build a default caption from the video's existing data.
  useEffect(() => {
    const base = video?.caption_draft
                 || video?.script?.cta
                 || video?.script?.hook
                 || `Today's pick: ${video?.product?.title || video?.niche || "must-have"}.`;
    const isAffiliate = (video?.mode || "affiliate") === "affiliate";
    const needsHashtags = !/#(ad|affiliate)/i.test(base);
    setCaption(needsHashtags && isAffiliate ? `${base}\n\n#ad #affiliate` : base);
  }, [video]);

  // Load connected accounts.
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/tiktok/accounts`);
        const list = r.data?.accounts || [];
        setAccounts(list);
        setEnvironment(r.data?.environment || "sandbox");
        if (list.length === 1) setSelected(new Set([list[0].id]));
      } catch (e) {
        toast.error("Failed to load TikTok accounts");
      }
    })();
  }, []);

  // Once we've fired a post, poll /api/tiktok/posts every 3s for live status.
  useEffect(() => {
    if (!results) return;
    const inFlight = results.filter((r) =>
      r.status === "uploading" || r.status === "queued");
    if (inFlight.length === 0) return;
    const t = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/tiktok/posts?limit=20`);
        const byId = new Map((r.data?.items || []).map((p) => [p.id, p]));
        setResults((prev) => prev.map((p) => byId.get(p.id) || p));
      } catch (_) {}
    }, 3000);
    return () => clearInterval(t);
  }, [results]);

  const toggle = (id) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const submit = async () => {
    if (selected.size === 0) { toast.error("Pick at least one account"); return; }
    if (!caption.trim()) { toast.error("Caption can't be empty"); return; }
    setPosting(true);
    try {
      const r = await axios.post(`${API}/tiktok/post`, {
        video_id: video.id,
        tiktok_account_ids: Array.from(selected),
        caption: caption.trim(),
        post_mode: "direct",
      });
      setResults(r.data?.posts || []);
      toast.success(`Submitted to ${r.data?.posts?.length || 0} account(s)`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.error === "compliance_blocked") {
        const lint = detail.lint || {};
        const issues = [
          ...(lint.banned_terms_found || []),
          ...(lint.fake_scarcity_terms || []),
          ...(lint.political_topics_found || []),
          ...(lint.financial_advice_found || []),
        ];
        toast.error(`Compliance blocked: ${issues.join(", ") || "missing #ad disclosure"}`,
                    { duration: 12000 });
      } else if (detail?.error === "daily_quota_exceeded") {
        const offenders = (detail.quota_report || [])
          .filter((q) => q.posted_today >= q.limit)
          .map((q) => `account ${q.account_id.slice(0,8)} (${q.posted_today}/${q.limit})`);
        toast.error(`Daily quota hit on: ${offenders.join(", ")}. Try after 00:00 UTC.`,
                    { duration: 14000 });
      } else if (detail?.error === "missing_storage") {
        // Phase 5.2c-pre — file lost (legacy job from before persistent
        // storage rolled out). Surface a clear re-render CTA instead of
        // an opaque 404.
        setMissingStorage(detail);
      } else {
        toast.error(`Post failed (HTTP ${e?.response?.status || "?"}): ${typeof detail === "string" ? detail : formatApiError(detail) || "unknown"}`,
                    { duration: 12000 });
      }
    } finally { setPosting(false); }
  };

  const statusLabel = (s) => {
    if (s === "queued") return "Queued…";
    if (s === "uploading") return "Uploading to TikTok…";
    if (s === "posted") return environment === "sandbox"
      ? "Published as private (Sandbox)" : "Published";
    if (s === "failed") return "Failed";
    return s || "—";
  };
  const statusTone = (s) => ({
    posted: "text-emerald-300", failed: "text-rose-300",
    uploading: "text-blue-300", queued: "text-zinc-300",
  }[s] || "text-zinc-400");

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="post-tiktok-modal">
      <div className="ap-panel max-w-lg w-full p-5 max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold flex items-center gap-2">
            <Music className="h-4 w-4 text-rose-400" /> Post to TikTok
          </h2>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200"
                  data-testid="post-tiktok-close"><X className="h-4 w-4" /></button>
        </div>

        {environment === "sandbox" && (
          <div className="rounded-md border border-amber-700/60 bg-amber-950/40 p-3 mb-4 flex items-start gap-2"
               data-testid="post-tiktok-sandbox-banner">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-300 mt-0.5 shrink-0" />
            <div className="text-[11px] text-amber-200 leading-relaxed">
              <strong>Sandbox mode</strong> — posts will be PRIVATE and only visible to your authorized
              test users until production review approves the app.
            </div>
          </div>
        )}

        {missingStorage ? (
          <div className="rounded-md border border-rose-700/60 bg-rose-950/40 p-4 space-y-3"
               data-testid="post-tiktok-missing-storage">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-rose-300 mt-0.5 shrink-0" />
              <div className="text-xs text-rose-200 leading-relaxed">
                <div className="font-semibold mb-1">This video's file was lost.</div>
                <div className="text-rose-300/80">
                  It was rendered before persistent storage was enabled and got wiped by a later
                  deploy. Re-render it once and it'll live in object storage from now on.
                </div>
              </div>
            </div>
            <button
              onClick={rerenderAndRetry}
              disabled={rerendering}
              className="w-full px-3 py-2 rounded-md bg-rose-500 hover:bg-rose-400 text-zinc-950 text-xs font-semibold disabled:opacity-60"
              data-testid="rerender-and-post-btn"
            >
              {rerendering ? "Queuing re-render…" : "Re-render and post"}
            </button>
          </div>
        ) : !results ? (
          <>
            <div className="mb-3">
              <div className="text-[10px] mono uppercase text-zinc-500 mb-2">Target accounts</div>
              {accounts.length === 0 ? (
                <div className="text-xs text-zinc-500">No connected accounts. Connect one in Settings first.</div>
              ) : (
                <div className="space-y-1.5">
                  {accounts.map((a) => (
                    <label key={a.id}
                           className={`flex items-center gap-2.5 p-2 rounded border cursor-pointer transition-colors ${
                             selected.has(a.id)
                               ? "border-emerald-700/60 bg-emerald-500/10"
                               : "border-zinc-800 bg-zinc-900 hover:bg-zinc-800/50"
                           }`}
                           data-testid={`post-tiktok-acc-${a.id}`}>
                      <input type="checkbox" checked={selected.has(a.id)} onChange={() => toggle(a.id)}
                             className="accent-emerald-500" />
                      {a.avatar_url
                        ? <img src={a.avatar_url} alt="" className="h-6 w-6 rounded-full" />
                        : <div className="h-6 w-6 rounded-full bg-zinc-800" />}
                      <div className="min-w-0 flex-1">
                        <div className="text-xs text-zinc-100 truncate">{a.display_name || (a.username ? `@${a.username}` : "TikTok account")}</div>
                        <div className="text-[10px] mono text-zinc-500 truncate">{a.username ? `@${a.username}` : `${(a.open_id || "").slice(0, 10)}…`}</div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="mb-4">
              <div className="text-[10px] mono uppercase text-zinc-500 mb-2 flex justify-between">
                <span>Caption</span>
                <span className={caption.length > 2200 ? "text-rose-400" : ""}>{caption.length}/2200</span>
              </div>
              <Textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={5}
                        className="bg-zinc-950 border-zinc-800 text-zinc-100 text-sm"
                        data-testid="post-tiktok-caption" />
            </div>

            <Button onClick={submit}
                    disabled={posting || accounts.length === 0 || selected.size === 0}
                    className="w-full bg-rose-500 hover:bg-rose-400 text-zinc-950 font-semibold"
                    data-testid="post-tiktok-submit">
              {posting
                ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Submitting…</>
                : <><Send className="h-3.5 w-3.5 mr-1.5" /> Post now to {selected.size || "—"} account{selected.size === 1 ? "" : "s"}</>}
            </Button>
          </>
        ) : (
          <div className="space-y-2" data-testid="post-tiktok-status">
            {results.map((r) => {
              const acc = accounts.find((a) => a.id === r.tiktok_account_id);
              return (
                <div key={r.id} className="ap-panel-2 p-3 flex items-center justify-between gap-2"
                     data-testid={`post-tiktok-result-${r.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs text-zinc-100 truncate">{acc?.display_name || r.tiktok_open_id}</div>
                    <div className={`text-[11px] mono ${statusTone(r.status)}`}>
                      {r.status === "uploading" || r.status === "queued"
                        ? <Loader2 className="h-3 w-3 inline mr-1 animate-spin" />
                        : r.status === "posted"
                          ? <Check className="h-3 w-3 inline mr-1" />
                          : r.status === "failed"
                            ? <X className="h-3 w-3 inline mr-1" />
                            : null}
                      {statusLabel(r.status)}
                    </div>
                    {r.error && <div className="text-[10px] text-rose-400 mt-1 break-words">{r.error}</div>}
                  </div>
                  <Badge variant="outline" className="border-zinc-700 text-[10px] mono text-zinc-500 shrink-0">
                    {r.publish_id ? r.publish_id.slice(0, 8) : "—"}
                  </Badge>
                </div>
              );
            })}
            <Button onClick={onClose} variant="outline"
                    className="w-full border-zinc-700 bg-zinc-900 text-zinc-200 mt-2">
              Close
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
